Bindable from ../bindable.civet

{ deprecationWarning, promiseChoke } from ../util.civet

pinvoke := <T extends Record<string, (...args: any[]) => any>>(object: T, method: keyof T, ...params: Parameters<T[typeof method]>) =>
  new Promise (resolve, reject) =>
    object[method] params..., (err: any, result: any) =>
      if err
        reject err
        return

      resolve result

delimiter := "/"

// TODO
type S3Bucket = any
type S3Object = any

export class S3FS with Bindable
  id: string
  bucket: S3Bucket

  @(@id: string, @bucket: S3Bucket, refreshCredentials?: => Promise<void>)
    super()
    refreshCredentials ?= -> Promise.reject new Error "No method given to refresh credentials automatically"
    refreshCredentialsPromise .= Promise.resolve()

    // TODO: convert and test this
    do (oldPromiseInvoke=pinvoke) ->
      pinvoke := (...args: any[]) ->
        try
          // Guard for expired credentials
          await refreshCredentialsPromise

          //@ts-expect-error
          await oldPromiseInvoke.apply(null, args)
        catch e
          if e is like { code: "CredentialsError" }
            console.info "Refreshing credentials after CredentialsError", e
            refreshCredentialsPromise = refreshCredentials!()

            await refreshCredentialsPromise
            // Retry calls after refreshing expired credentials
            //@ts-expect-error
            oldPromiseInvoke.apply(null, args)
          else
            throw e


  notify<T>(eventType: string, path: string)
    (result: T) =>
      @trigger eventType, path
      return result

  clearCache()
    localCache.clear()
    metaCache.clear()

  read(path: string)
    unless path.startsWith delimiter
      path = delimiter + path

    key := `${@id}${path}`

    getRemote @bucket, key
    .then @notify "read", path

  write(path: string, blob: Blob, options?: UploadOptions)
    unless path.startsWith delimiter
      path = delimiter + path

    key := `${@id}${path}`

    uploadToS3 @bucket, key, blob, options
    .then @notify "write", path

  delete(path: string)
    unless path.startsWith delimiter
      path = delimiter + path

    key := `${@id}${path}`

    deleteFromS3 @bucket, key
    .then @notify "delete", path

  list(dir="/")
    unless dir.startsWith delimiter
      dir = `${delimiter}${dir}`

    unless dir.endsWith delimiter
      dir = `${dir}${delimiter}`

    list dir, @bucket, @id
    .then @notify "list", dir

type MetaInfo =
  ContentType: string
  LastModified: Date

localCache := new Map<string, Blob | Error>
metaCache := new Map<string, MetaInfo>

type UploadOptions = {
  cacheControl?: number
}

uploadToS3 := (bucket: S3Bucket, key: string, file: Blob, options: UploadOptions={}) =>
  { cacheControl } .= options

  cacheControl ?= 0

  // Optimistically Cache
  localCache.set key, file
  metaCache.set key,
    ContentType: file.type
    LastModified: new Date

  pinvoke bucket, "putObject",
    Key: key
    ContentType: file.type
    Body: file
    CacheControl: `max-age=${cacheControl}`

getRemote := (bucket: S3Bucket, key: string) =>
  cachedItem := localCache.get(key)

  if cachedItem
    if cachedItem instanceof Blob
      return Promise.resolve(cachedItem)
    else
      return Promise.reject(cachedItem)

  try
    data := await pinvoke bucket, "getObject",
      Key: key

    //@ts-expect-error TODO
    {Body, ContentType} := data

    blob := new Blob [Body],
      type: ContentType

    localCache.set key, blob

    return blob

  catch e
    // Cache Not Founds too, since that's often what is slow
    localCache.set key, e as Error
    throw e

deleteFromS3 := (bucket: S3Bucket, key: string) =>
  localCache.set key, new Error "Not Found"
  metaCache.delete key

  pinvoke bucket, "deleteObject",
    Key: key

list := promiseChoke (dir: string, bucket: S3Bucket, id: string) ->
  prefix := `${id}${dir}`

  result: any := await pinvoke bucket, "listObjects",
    Prefix: prefix
    Delimiter: delimiter

  results: Entry[] := result.CommonPrefixes.map (p: any) ->
    FolderEntry p.Prefix, id, prefix
  .concat result.Contents.map (o: any) ->
    FileEntry o, id, prefix
  .map (entry: Entry) ->
    fetchMeta(entry, bucket)

  Promise.all results

fetchFileMeta := (key: string, bucket: S3Bucket) ->
  cachedItem := metaCache.get(key)

  if cachedItem
    return Promise.resolve(cachedItem)

  result := await pinvoke bucket, "headObject",
    Key: key
  |> as MetaInfo

  metaCache.set key, result

  return result

fetchMeta := (entry: Entry, bucket: S3Bucket) ->
  if "folder" in entry
    return entry

  meta := await fetchFileMeta(entry.remotePath, bucket)

  entry.type = meta.ContentType
  entry.updatedAt = new Date(meta.LastModified)

  return entry

FolderEntry := (path: string, id: string, prefix: string) ->
  folder: true
  path: path.replace(id, "")
  relativePath: path.replace(prefix, "")
  remotePath: path

FileEntry := (object: S3Object, id: string, prefix: string) ->
  path := object.Key

  path: path.replace(id, "")
  relativePath: path.replace(prefix, "")
  remotePath: path
  size: object.Size
  type: undefined as string?
  updatedAt: undefined as Date?

type Entry = ReturnType<typeof FileEntry | typeof FolderEntry>

export default S3FS
